import { Platform } from 'react-native';

const Config = require('~config/default.env').default;

/**
 * Error logger utility for capturing and reporting exceptions
 * with improved console formatting and colors
 */
class ErrorLogger {
  constructor(options = {}) {
    this.options = {
      environment: Config.ENVIRONMENT,
      enableConsoleLogging: true,
      platform: Platform.OS === 'web' ? 'web' : 'app',
      ...options,
    };

    // Console color codes
    this.colors = {
      error: '\x1b[31m',
      warning: '\x1b[33m',
      info: '\x1b[36m',
      magenta: '\x1b[35m',
      white: '\x1b[37m',
      reset: '\x1b[0m',
    };
  }

  /**
   * Captures and processes an exception
   * @param {Error|string} error - The error object or message to capture
   * @param {Object} context - Additional context information about the error
   */
  captureException(error, context = {}) {
    const typePlatform = `[LILLA-${this.options.platform.toUpperCase()}]`;
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    const errorInfo = {
      message: errorObj.message,
      stack: errorObj.stack?.split('\n')[0] || 'No stack trace',
      name: errorObj.name,
      timestamp: new Date().toISOString(),
      environment: this.options.environment,
      ...context,
    };

    // Log to console in development
    if (this.options.enableConsoleLogging) {
      // Use if in browser environment
      if (typeof window !== 'undefined') {
        // Header
        console.log(
          `%c${typePlatform}[LOGGER] Error captured:`,
          'color: magenta; font-weight: bold; font-size: 12px;',
        );

        // Display error info as a table
        console.table(errorInfo);

        // Display full stack trace
        console.log('%cStack Trace:', 'color: red; font-weight: bold;');
        console.log(
          '%c' + (errorObj.stack || 'No stack trace available'),
          'color: white;',
        );
      } else {
        console.log(
          `${this.colors.magenta}${typePlatform}[LOGGER] Error captured:${this.colors.reset}`,
        );
        console.table(errorInfo);
        console.log(`${this.colors.error}Stack Trace:${this.colors.reset}`);
        console.log(
          `${this.colors.white}${errorObj.stack || 'No stack trace available'}${
            this.colors.reset
          }`,
        );
      }
    }

    return errorInfo;
  }

  /**
   * Log information message
   * @param {string} message - The message to log
   * @param {Object} data - Additional data to include in the log
   */
  logInfo(message, data = {}) {
    if (this.options.enableConsoleLogging) {
      const typePlatform = `[LILLA-${this.options.platform.toUpperCase()}]`;

      if (typeof window !== 'undefined') {
        console.log(
          `%c${typePlatform}[INFO] ${message}`,
          'color: magenta; font-weight: bold;',
        );
        if (Object.keys(data).length > 0) {
          console.table(data);
        }
      } else {
        console.log(
          `${this.colors.magenta}${typePlatform}[INFO] ${message}${this.colors.reset}`,
        );
        if (Object.keys(data).length > 0) {
          console.table(data);
        }
      }
    }
  }
}

export default ErrorLogger;
